import React, { useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { ApplicationAction } from "../../model_gen/application";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { X, Plus } from "lucide-react";

interface NewIntentModalProps {
  isOpen: boolean;
  applicationId: number;
  onClose: () => void;
  onIntentCreated: (intent: ApplicationAction) => void;
}

const NewIntentModal: React.FC<NewIntentModalProps> = ({
  isOpen,
  applicationId,
  onClose,
  onIntentCreated,
}) => {
  const { apiKey, companyId } = usePermissions();
  const [formData, setFormData] = useState({
    action_type: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey) {
      setError("Missing API key");
      return;
    }
    if (!applicationId) {
      setError("Please select an application first");
      return;
    }
    if (!formData.action_type.trim()) {
      setError("Intent name is required");
      return;
    }

    const intentData: any = {
      action_type: formData.action_type.trim(),
      application_id: applicationId,
      ...(companyId ? { company_id: companyId } : {}),
      ...(formData.description.trim()
        ? { meta: { metadata: { summary: formData.description.trim() } } }
        : {}),
    };

    ApplicationService.createApplicationAction(
      applicationId,
      intentData,
      setIsLoading,
      apiKey
    ).subscribe(
      (response: ApplicationAction) => {
        onIntentCreated(response);
        handleClose();
      },
      (err: any) => {
        const message =
          typeof err === "string"
            ? err
            : err?.message || "Failed to create intent";
        setError(message);
        setIsLoading(false);
      }
    );
  };

  const handleClose = () => {
    setFormData({
      action_type: "",
      description: "",
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-lg max-w-md w-full mx-4">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-gc-med">
              Create New Intent
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="intent_name">Intent Name</Label>
                <Input
                  id="intent_name"
                  type="text"
                  placeholder="e.g., customer_support, booking"
                  value={formData.action_type}
                  onChange={(e) => handleInputChange("action_type", e.target.value)}
                  className="w-full"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="intent_description">Description (Optional)</Label>
                <Textarea
                  id="intent_description"
                  placeholder="Brief description of what this intent does..."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="w-full min-h-[80px]"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.action_type.trim() || isLoading}
                  className="flex-1 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {isLoading ? "Creating..." : "Create Intent"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewIntentModal;

